/** SUNTRONIX ONE — Apple CTO/BTO Configurator (Sales Desk). */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Apple, Cpu, Save, Star, Plus, ChevronRight, ShieldCheck, AlertTriangle } from "lucide-react";
import AppleCompatStudio from "@/pages/sales/AppleCompatStudio";

const fmtINR = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR",
    maximumFractionDigits: 0 }).format(n);


export default function AppleConfigurator() {
  const [sp] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [family, setFamily] = useState("MacBook Pro");
  const [country, setCountry] = useState("IN");
  const [bases, setBases] = useState([]);
  const [baseId, setBaseId] = useState(null);
  const [options, setOptions] = useState([]);
  const [selections, setSelections] = useState({});
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState([]);
  const [qty, setQty] = useState(1);
  const [quotationId, setQuotationId] = useState(sp.get("quote") || "");
  const [customerPartNumber, setCustomerPartNumber] = useState("");
  const [seeding, setSeeding] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [compatOpen, setCompatOpen] = useState(false);

  useEffect(() => { api.get("/apple/meta").then(({ data }) => setMeta(data)); }, []);
  useEffect(() => {
    api.get("/apple/base-models", { params: { family, country } })
      .then(({ data }) => setBases(data.items || []));
  }, [family, country]);
  useEffect(() => {
    if (!baseId) { setOptions([]); setSelections({}); setResult(null); return; }
    api.get("/apple/options", { params: { base_model_id: baseId } })
      .then(({ data }) => setOptions(data.items || []));
  }, [baseId]);
  const loadSaved = useCallback(() =>
    api.get("/apple/configs", { params: { family } }).then(({ data }) => setSaved(data.items || [])),
    [family]);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const rebuild = useCallback(async () => {
    if (!baseId) return;
    try {
      const { data } = await api.post("/apple/build",
        { base_model_id: baseId, selections, quantity: qty });
      setResult(data);
    } catch (e) { setResult(null); }
  }, [baseId, selections, qty]);
  useEffect(() => { rebuild(); }, [rebuild]);

  const saveConfig = async () => {
    const name = window.prompt("Save this configuration as:");
    if (!name) return;
    try {
      await api.post("/apple/configs",
        { name, base_model_id: baseId, selections, favourite: false,
          customer_part_number: customerPartNumber || null });
      toast.success("Saved");
      loadSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const addToQuote = async () => {
    if (!quotationId) { toast.error("Enter a quotation id first"); return; }
    try {
      const { data } = await api.post("/apple/add-to-quote",
        { quotation_id: quotationId, base_model_id: baseId,
          selections, quantity: qty,
          customer_part_number: customerPartNumber || null });
      toast.success(`Added — line total ${fmtINR(data.line.line_total)}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const seedCatalogue = async () => {
    if (!window.confirm("Seed the Apple catalogue for " + country + "? " +
        "This loads the 2026 lineup (11 base models + 55 option groups) " +
        "and can be safely re-run.")) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/apple/seed-catalogue", { country, force: false });
      toast.success(`Seeded ${data.inserted_bases.length} base models · ${data.inserted_options} option groups`);
      // reload bases
      const { data: bd } = await api.get("/apple/base-models", { params: { family, country } });
      setBases(bd.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Seed failed"); }
    finally { setSeeding(false); }
  };

  const loadSavedConfig = (c) => {
    setBaseId(c.base_model_id); setSelections(c.selections || {});
  };

  const compat = result?.compat;
  const isValueBlocked = (kind, val) => {
    const c = compat?.constraints?.[kind];
    if (!c) return false;
    if (c.allowed && !c.allowed.includes(val)) return true;
    if ((c.forbidden || []).includes(val)) return true;
    return false;
  };

  return (
    <div className="max-w-[1500px] mx-auto space-y-4" data-testid="apple-cfg">
      <header className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-slate-900 text-white grid place-items-center">
          <Apple className="h-5 w-5" />
        </div>
        <div>
          <div className="text-xl font-heading font-semibold">Apple Configurator</div>
          <div className="text-[11px] text-slate-500">
            Configure custom Mac builds · India pricing pilot
          </div>
        </div>
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        <select value={family} onChange={(e) => { setFamily(e.target.value); setBaseId(null); }}
            data-testid="apple-family"
            className="text-[12px] border border-slate-200 rounded-md px-2 py-1.5">
          {(meta?.families || []).map((f) => <option key={f.id}>{f.name}</option>)}
        </select>
        <select value={country} onChange={(e) => setCountry(e.target.value)}
            data-testid="apple-country"
            className="text-[12px] border border-slate-200 rounded-md px-2 py-1.5">
          {(meta?.countries || ["IN"]).map((c) => <option key={c}>{c}</option>)}
        </select>
        <input type="number" min={1} value={qty}
            onChange={(e) => setQty(parseInt(e.target.value) || 1)}
            data-testid="apple-qty"
            className="w-20 text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
        <input value={quotationId} onChange={(e) => setQuotationId(e.target.value)}
            placeholder="Quotation ID (paste)"
            data-testid="apple-quote-id"
            className="text-[12px] border border-slate-200 rounded-md px-2 py-1.5 flex-1 min-w-[260px]" />
        {meta?.current_user?.can_write && (
          <>
            <button onClick={() => setManageOpen(true)}
                data-testid="apple-manage-bases"
                className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50">
              Manage Base Models
            </button>
            <button onClick={() => setCompatOpen(true)}
                data-testid="apple-compat-open"
                className="text-[11px] px-2.5 py-1.5 rounded-md border border-emerald-600 text-emerald-800 hover:bg-emerald-50 inline-flex items-center gap-1">
              <ShieldCheck className="h-3 w-3" /> Compatibility Studio
            </button>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Base model + options */}
        <div className="md:col-span-2 space-y-3">
          <div className="border border-slate-200 rounded-lg bg-white p-3">
            <div className="text-[11px] font-semibold text-slate-700 mb-2">Base model</div>
            {bases.length === 0 ? (
              <div className="text-[11px] text-slate-500 space-y-2">
                <div>No {family} base models yet for {country}.</div>
                {meta?.current_user?.can_admin ? (
                  <button onClick={seedCatalogue} disabled={seeding}
                      data-testid="apple-seed-catalogue"
                      className="text-[11px] px-2.5 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-50">
                    {seeding ? "Seeding…" : "Seed 2026 Apple catalogue"}
                  </button>
                ) : (
                  <div className="text-slate-400">
                    Ask an authorised admin to seed the catalogue, or import a CSV via
                    /api/apple/import.
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {bases.map((b) => (
                  <button key={b.id} onClick={() => setBaseId(b.id)}
                      data-testid={`apple-base-${b.id}`}
                      className={"text-left border rounded-md p-2.5 " +
                          (baseId === b.id ? "border-emerald-500 bg-emerald-50"
                                                                : "border-slate-200 hover:bg-slate-50")}>
                    <div className="text-[12px] font-semibold">{b.model_name}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {b.base_chip} · {b.base_memory} · {b.base_storage}
                    </div>
                    <div className="text-[10px] mt-1 flex items-center gap-1">
                      <span className="font-mono">{fmtINR(b.base_price_mrp)}</span>
                      <span className={"text-[9px] px-1 py-0.5 rounded " +
                          (b.freshness === "OUTDATED" ? "bg-rose-100 text-rose-800"
                              : b.freshness === "REVIEW_DUE" ? "bg-amber-100 text-amber-800"
                              : "bg-emerald-100 text-emerald-800")}>
                        {b.freshness}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {baseId && (
            <div className="border border-slate-200 rounded-lg bg-white p-3 space-y-2"
                data-testid="apple-options">
              <div className="text-[11px] font-semibold text-slate-700">Options</div>
              {options.length === 0 ? (
                <div className="text-[11px] text-slate-400">
                  No options defined for this base — MRP will match the base price.
                </div>
              ) : options.map((opt) => (
                <div key={opt.id} className="grid grid-cols-4 gap-2 items-center"
                    data-testid={`apple-opt-${opt.kind}`}>
                  <div className="text-[11px] font-semibold col-span-1">
                    {opt.kind.replace(/_/g, " ")}
                    {opt.mandatory && <span className="text-rose-600 ml-1">*</span>}
                  </div>
                  <select value={selections[opt.kind] || ""}
                      onChange={(e) => setSelections({ ...selections, [opt.kind]: e.target.value })}
                      className="col-span-3 text-[11px] border border-slate-200 rounded-md px-2 py-1">
                    <option value="">— pick —</option>
                    {opt.values.map((v) => {
                      const blocked = isValueBlocked(opt.kind, v.value) &&
                        selections[opt.kind] !== v.value;
                      return (
                        <option key={v.value} value={v.value} disabled={blocked}>
                          {v.label || v.value}
                          {v.price_delta ? ` (+${fmtINR(v.price_delta)})` : ""}
                          {blocked ? " — incompatible" : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Summary + saved */}
        <div className="space-y-3">
          {compat?.warn_violations?.length > 0 && (
            <div className="border border-amber-300 bg-amber-50 rounded-lg p-3"
                data-testid="apple-compat-warnings">
              <div className="text-[11px] font-semibold text-amber-900 flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" /> Compatibility warnings
              </div>
              <ul className="list-disc pl-4 mt-1 text-[10.5px] text-amber-800 space-y-0.5">
                {compat.warn_violations.map((w, i) => <li key={i}>{w.message}</li>)}
              </ul>
              <div className="text-[9.5px] text-amber-700 mt-1 font-semibold">
                This configuration will be blocked at "Add to quotation" until resolved.
              </div>
            </div>
          )}
          <div className="border border-slate-200 rounded-lg bg-white p-3" data-testid="apple-summary">
            <div className="text-[11px] font-semibold text-slate-700 mb-2">Configuration summary</div>
            {!result || !result.ok ? (
              <div className="text-[11px] text-slate-500">
                {result?.errors?.length
                  ? <ul className="list-disc pl-4 text-rose-700">{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                  : "Pick a base model and options to see pricing."}
              </div>
            ) : (
              <>
                <div className="text-[12px] font-semibold">{result.spec_summary}</div>
                <div className="mt-2 space-y-0.5 text-[11px]">
                  {result.price_breakdown.map((b, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-slate-600">{b.label}</span>
                      <span className="font-mono">{fmtINR(b.delta)}</span>
                    </div>
                  ))}
                  <div className="border-t border-slate-200 pt-1 mt-1 flex justify-between font-semibold">
                    <span>MRP total</span><span className="font-mono">{fmtINR(result.mrp_total)}</span>
                  </div>
                  <div className="flex justify-between text-slate-500">
                    <span>GST {result.gst_pct}%</span>
                    <span className="font-mono">{fmtINR(result.gst_amount)}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-emerald-800">
                    <span>Landed MRP</span>
                    <span className="font-mono">{fmtINR(result.landed_mrp_total)}</span>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">
                    Lead time: {result.lead_time?.days ?? "—"}d · {result.lead_time?.kind}
                  </div>
                </div>
                <label className="text-[10px] text-slate-500 block mt-3">
                  Customer / Internal Part Number (optional)
                  <input value={customerPartNumber}
                      onChange={(e) => setCustomerPartNumber(e.target.value)}
                      placeholder={result.base?.base_part_number || "e.g. SUN-MBP14-M4P-24-512"}
                      data-testid="apple-customer-part"
                      className="mt-1 w-full text-[11.5px] font-mono border border-slate-200 rounded-md px-2 py-1.5" />
                  <div className="text-[9.5px] text-slate-400 mt-0.5">
                    Overrides the Apple part number on the quotation line and saved config.
                    Apple base part remains stamped internally for traceability.
                  </div>
                </label>
                <div className="flex items-center gap-2 mt-3">
                  <button onClick={saveConfig} data-testid="apple-save"
                      className="text-[11px] px-2.5 py-1 border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1">
                    <Save className="h-3 w-3" /> Save
                  </button>
                  <button onClick={addToQuote} data-testid="apple-add-to-quote"
                      disabled={compat?.warn_violations?.length > 0}
                      title={compat?.warn_violations?.length > 0
                        ? "Resolve compatibility warnings first" : undefined}
                      className="text-[11px] px-2.5 py-1 bg-emerald-800 text-white rounded inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                    <Plus className="h-3 w-3" /> Add to quotation
                  </button>
                </div>
              </>
            )}
          </div>
          <div className="border border-slate-200 rounded-lg bg-white p-3">
            <div className="text-[11px] font-semibold text-slate-700 mb-2 flex items-center gap-1">
              <Star className="h-3 w-3 text-amber-500" /> Saved configurations ({saved.length})
            </div>
            <div className="space-y-1 max-h-[300px] overflow-y-auto">
              {saved.map((c) => (
                <button key={c.id} onClick={() => loadSavedConfig(c)}
                    data-testid={`apple-saved-${c.id}`}
                    className="w-full text-left p-1.5 rounded hover:bg-slate-50 text-[11px]">
                  <div className="font-semibold">{c.name}</div>
                  <div className="text-[10px] text-slate-500 truncate">{c.spec_summary}</div>
                  <div className="text-[10px] font-mono">{fmtINR(c.mrp_total)}</div>
                </button>
              ))}
              {!saved.length && <div className="text-[11px] text-slate-400">Nothing saved yet.</div>}
            </div>
          </div>
        </div>
      </div>
      {compatOpen && (
        <AppleCompatStudio meta={meta}
          onClose={() => { setCompatOpen(false); rebuild(); }} />
      )}
      {manageOpen && (
        <ManageBaseModelsDrawer
          bases={bases} family={family} country={country}
          onClose={() => setManageOpen(false)}
          onChanged={async () => {
            // Refresh base list after any add/edit/delete
            try {
              const { data } = await api.get("/apple/base-models", { params: { family, country } });
              setBases(data.items || []);
            } catch (e) { /* toast handled by drawer */ }
          }} />
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Manage Base Models — Add / Edit / Delete drawer
// ---------------------------------------------------------------------------
function ManageBaseModelsDrawer({ bases, family, country, onClose, onChanged }) {
  const [editing, setEditing] = useState(null);   // row being edited (or "new")
  const [busy, setBusy] = useState(false);

  const startNew = () => setEditing({
    __new: true,
    family, country,
    model_name: "", generation: "",
    base_chip: "", base_memory: "", base_storage: "",
    base_part_number: "", base_price_mrp: 0,
    lead_time_kind: "STOCK", lead_time_days: 3,
    colour_options: [], active: true,
  });

  const save = async () => {
    if (!editing.model_name) { toast.error("Model name required"); return; }
    setBusy(true);
    try {
      if (editing.__new) {
        await api.post("/apple/base-models", {
          family: editing.family, model_name: editing.model_name,
          generation: editing.generation, country: editing.country,
          base_chip: editing.base_chip, base_memory: editing.base_memory,
          base_storage: editing.base_storage,
          base_part_number: editing.base_part_number,
          base_price_mrp: Number(editing.base_price_mrp) || 0,
          lead_time_kind: editing.lead_time_kind,
          lead_time_days: Number(editing.lead_time_days) || null,
          colour_options: editing.colour_options,
          active: editing.active,
        });
        toast.success("Base model added");
      } else {
        await api.patch(`/apple/base-models/${editing.id}`, {
          model_name: editing.model_name, generation: editing.generation,
          base_chip: editing.base_chip, base_memory: editing.base_memory,
          base_storage: editing.base_storage,
          base_part_number: editing.base_part_number,
          base_price_mrp: Number(editing.base_price_mrp) || 0,
          lead_time_kind: editing.lead_time_kind,
          lead_time_days: Number(editing.lead_time_days) || null,
          active: editing.active,
        });
        toast.success(`Updated "${editing.model_name}"`);
      }
      setEditing(null);
      await onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const del = async (b, hard) => {
    const msg = hard
      ? `Permanently delete "${b.model_name}" and its options? This cannot be undone.`
      : `Deactivate "${b.model_name}"? (Soft-delete — can be re-activated later.)`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.delete(`/apple/base-models/${b.id}`, { params: { hard } });
      toast.success(hard ? "Deleted" : "Deactivated");
      await onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  const downloadCsv = (filename, text) => {
    const blob = new Blob([text], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/apple/catalogue/csv-template", { responseType: "text" });
      downloadCsv("apple-catalogue-template.csv", typeof data === "string" ? data : JSON.stringify(data));
      toast.success("Template downloaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Download failed"); }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/apple/catalogue/csv-export", {
        params: { country }, responseType: "text",
      });
      downloadCsv(`apple-catalogue-${country}-${new Date().toISOString().slice(0,10)}.csv`,
                  typeof data === "string" ? data : JSON.stringify(data));
      toast.success("Catalogue exported");
    } catch (err) { toast.error(err.response?.data?.detail || "Export failed"); }
  };

  const importCsv = async (file) => {
    if (!file) return;
    const csv_text = await file.text();
    const mode = window.confirm(
      "REPLACE mode will deactivate any current models NOT in your CSV.\n\n" +
      "OK = Replace (safest for a full lineup refresh)\n" +
      "Cancel = Upsert (only insert / update rows in the CSV, don't touch others)"
    ) ? "replace" : "upsert";
    setBusy(true);
    try {
      const { data } = await api.post("/apple/catalogue/csv-import", {
        csv_text, country, mode, dry_run: false,
      });
      toast.success(
        `Import: ${data.inserted_count} new · ${data.updated_count} updated · ` +
        `${data.deactivated_count} deactivated · ${data.error_count} errors`
      );
      if (data.errors?.length) {
        console.error("CSV import errors:", data.errors);
      }
      await onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="apple-manage-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-[720px] max-w-full h-full bg-white shadow-xl overflow-y-auto">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="text-[10px] uppercase text-slate-500">Admin</div>
            <div className="text-[14px] font-heading font-semibold">
              Manage Base Models · {family} · {country}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadTemplate}
                data-testid="apple-manage-csv-template"
                className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50">
              CSV Template
            </button>
            <button onClick={exportCsv}
                data-testid="apple-manage-csv-export"
                className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50">
              Export CSV
            </button>
            <label className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50 cursor-pointer"
                data-testid="apple-manage-csv-import-label">
              Import CSV
              <input type="file" accept=".csv,text/csv" className="hidden"
                  data-testid="apple-manage-csv-import"
                  onChange={(e) => importCsv(e.target.files?.[0])} />
            </label>
            <button onClick={startNew} disabled={busy || !!editing}
                data-testid="apple-manage-add"
                className="text-[11px] px-2.5 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-40">
              + Add
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
          </div>
        </div>

        {editing ? (
          <div className="p-4 space-y-3" data-testid="apple-manage-editor">
            <div className="text-[12px] font-heading font-semibold">
              {editing.__new ? "New base model" : `Editing: ${editing.model_name}`}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <EditField label="Model Name" value={editing.model_name}
                  testid="apple-edit-model" required
                  onChange={(v) => setEditing({ ...editing, model_name: v })} />
              <EditField label="Generation" value={editing.generation}
                  onChange={(v) => setEditing({ ...editing, generation: v })} />
              <EditField label="Chip" value={editing.base_chip}
                  onChange={(v) => setEditing({ ...editing, base_chip: v })} />
              <EditField label="Base Memory" value={editing.base_memory}
                  onChange={(v) => setEditing({ ...editing, base_memory: v })} />
              <EditField label="Base Storage" value={editing.base_storage}
                  onChange={(v) => setEditing({ ...editing, base_storage: v })} />
              <EditField label="Apple Part Number" value={editing.base_part_number}
                  testid="apple-edit-part" mono
                  onChange={(v) => setEditing({ ...editing, base_part_number: v })} />
              <EditField label="Base Price (MRP INR)" value={editing.base_price_mrp}
                  testid="apple-edit-price" type="number"
                  onChange={(v) => setEditing({ ...editing, base_price_mrp: v })} />
              <label className="text-[10px] text-slate-500 block">
                Lead Time Kind
                <select value={editing.lead_time_kind}
                    onChange={(e) => setEditing({ ...editing, lead_time_kind: e.target.value })}
                    className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5">
                  <option value="STOCK">STOCK (in stock)</option>
                  <option value="CTO_BTO">CTO/BTO (custom build)</option>
                </select>
              </label>
              <EditField label="Lead Time Days" value={editing.lead_time_days}
                  type="number"
                  onChange={(v) => setEditing({ ...editing, lead_time_days: v })} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)} disabled={busy}
                  className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">
                Cancel
              </button>
              <button onClick={save} disabled={busy || !editing.model_name}
                  data-testid="apple-manage-save"
                  className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-40">
                {busy ? "Saving…" : editing.__new ? "Create base model" : "Save changes"}
              </button>
            </div>
            {!editing.__new && (
              <OptionsEditor baseId={editing.id} />
            )}
          </div>
        ) : (
          <div className="p-4">
            <table className="w-full text-[11.5px]" data-testid="apple-manage-table">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left px-2 py-2">Model</th>
                  <th className="text-left px-2 py-2">Part Number</th>
                  <th className="text-left px-2 py-2">Price</th>
                  <th className="text-left px-2 py-2">Freshness</th>
                  <th className="text-right px-2 py-2 w-40"></th>
                </tr>
              </thead>
              <tbody>
                {bases.length === 0 && (
                  <tr><td colSpan={5} className="text-[11px] text-slate-400 px-2 py-6 text-center">
                    No base models in this family/country yet. Click Add above.
                  </td></tr>
                )}
                {bases.map((b) => (
                  <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50"
                        data-testid={`apple-manage-row-${b.id}`}>
                    <td className="px-2 py-2">
                      <div className="font-medium">{b.model_name}</div>
                      <div className="text-[10px] text-slate-500">
                        {b.base_chip} · {b.base_memory} · {b.base_storage}
                      </div>
                    </td>
                    <td className="px-2 py-2 font-mono text-[10.5px]">
                      {b.base_part_number || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-2 py-2 font-mono">{fmtINR(b.base_price_mrp)}</td>
                    <td className="px-2 py-2">
                      <span className={"text-[9px] px-1 py-0.5 rounded " +
                          (b.freshness === "OUTDATED" ? "bg-rose-100 text-rose-800"
                              : b.freshness === "REVIEW_DUE" ? "bg-amber-100 text-amber-800"
                              : "bg-emerald-100 text-emerald-800")}>
                        {b.freshness}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-right space-x-1">
                      <button onClick={() => setEditing({ ...b })}
                          data-testid={`apple-manage-edit-${b.id}`}
                          className="text-[10.5px] px-2 py-1 rounded border border-slate-300 hover:bg-slate-50">
                        Edit
                      </button>
                      <button onClick={() => del(b, true)}
                          data-testid={`apple-manage-delete-${b.id}`}
                          className="text-[10.5px] px-2 py-1 rounded border border-rose-500 text-rose-700 hover:bg-rose-50">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


function EditField({ label, value, onChange, type, mono, required, testid }) {
  return (
    <label className="text-[10px] text-slate-500 block">
      {label}{required ? " *" : ""}
      <input value={value ?? ""} type={type || "text"}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className={"mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5 " +
                      (mono ? "font-mono" : "")} />
    </label>
  );
}



// ---------------------------------------------------------------------------
// Options editor — nested inside Manage drawer.  Lets admins edit each
// option group's price deltas + labels, add rows, remove rows, and save
// the whole group atomically.
// ---------------------------------------------------------------------------
function OptionsEditor({ baseId }) {
  const [groups, setGroups] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/apple/options", { params: { base_model_id: baseId } });
      setGroups(data.items || []);
    } catch (err) { toast.error("Failed to load options"); }
  }, [baseId]);
  useEffect(() => { load(); }, [baseId, load]);

  const patchGroup = (gid, mutator) => {
    setGroups((gs) => gs.map((g) => g.id === gid ? mutator({ ...g, values: [...(g.values || [])] }) : g));
  };

  const saveGroup = async (g) => {
    setBusyId(g.id);
    try {
      await api.patch(`/apple/options/${g.id}`, {
        mandatory: g.mandatory,
        values: (g.values || []).map((v) => ({
          value: v.value, label: v.label || v.value,
          price_delta: Number(v.price_delta) || 0,
          part_number_suffix: v.part_number_suffix || null,
        })),
      });
      toast.success(`${g.kind} saved`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusyId(null); }
  };

  const deleteGroup = async (g) => {
    if (!window.confirm(`Delete the entire ${g.kind} option group? Buyers will no longer see this choice on the configurator.`)) return;
    setBusyId(g.id);
    try {
      await api.delete(`/apple/options/${g.id}`);
      toast.success(`${g.kind} deleted`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally { setBusyId(null); }
  };

  return (
    <div className="border-t border-slate-200 mt-4 pt-4 space-y-3" data-testid="apple-manage-options">
      <div className="text-[12px] font-heading font-semibold">
        CTO Options — edit price deltas and choices
      </div>
      {groups.length === 0 && (
        <div className="text-[11px] text-slate-400">No option groups on this model yet.</div>
      )}
      {groups.map((g) => (
        <div key={g.id} className="rounded-md border border-slate-200 bg-slate-50 p-3"
              data-testid={`apple-optgrp-${g.id}`}>
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[11.5px] font-semibold">{g.kind}</div>
            <label className="text-[10px] text-slate-500 flex items-center gap-1">
              <input type="checkbox" checked={!!g.mandatory}
                  onChange={(e) => patchGroup(g.id, (x) => ({ ...x, mandatory: e.target.checked }))} />
              mandatory
            </label>
            <div className="flex-1" />
            <button onClick={() => saveGroup(g)} disabled={busyId === g.id}
                data-testid={`apple-optgrp-save-${g.id}`}
                className="text-[10.5px] px-2 py-1 rounded bg-slate-900 text-white disabled:opacity-40">
              {busyId === g.id ? "…" : "Save"}
            </button>
            <button onClick={() => deleteGroup(g)} disabled={busyId === g.id}
                className="text-[10.5px] px-2 py-1 rounded border border-rose-500 text-rose-700 hover:bg-rose-50">
              Delete
            </button>
          </div>
          <table className="w-full text-[11px]">
            <thead className="text-slate-500">
              <tr>
                <th className="text-left px-1 py-1 w-[45%]">Value</th>
                <th className="text-left px-1 py-1">Price delta (₹)</th>
                <th className="text-right px-1 py-1 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {(g.values || []).map((v, i) => (
                <tr key={i}>
                  <td className="px-1 py-1">
                    <input value={v.value || ""}
                        onChange={(e) => patchGroup(g.id, (x) => {
                          x.values[i] = { ...x.values[i], value: e.target.value };
                          return x;
                        })}
                        data-testid={`apple-optval-${g.id}-${i}-value`}
                        className="w-full text-[11px] border border-slate-200 rounded px-1.5 py-1 bg-white" />
                  </td>
                  <td className="px-1 py-1">
                    <input type="number" value={v.price_delta ?? 0}
                        onChange={(e) => patchGroup(g.id, (x) => {
                          x.values[i] = { ...x.values[i], price_delta: e.target.value };
                          return x;
                        })}
                        data-testid={`apple-optval-${g.id}-${i}-price`}
                        className="w-full text-[11px] font-mono border border-slate-200 rounded px-1.5 py-1 bg-white" />
                  </td>
                  <td className="px-1 py-1 text-right">
                    <button onClick={() => patchGroup(g.id, (x) => {
                              x.values = x.values.filter((_, j) => j !== i);
                              return x;
                            })}
                        className="text-[10px] text-rose-600 hover:underline">
                      remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={() => patchGroup(g.id, (x) => {
                    x.values.push({ value: "", price_delta: 0 });
                    return x;
                  })}
              data-testid={`apple-optgrp-addval-${g.id}`}
              className="mt-2 text-[10.5px] px-2 py-1 rounded border border-dashed border-slate-300 hover:bg-white">
            + Add option value
          </button>
        </div>
      ))}
    </div>
  );
}

